import { ModalsContext } from '@ws/shared/components';
import { TNote } from '@ws/shared/types';
import { useCallback, useContext, useMemo } from 'react';

import { EditabilityContext } from '../../../context/EditabilityContext';
import { INoteSplitterChildrenParams, TChildren, TOnChange } from '../types';

import { showSplitModal } from './SplitModal';

interface INoteSplitterExtensionProps {
  note: TNote;
  children: TChildren<INoteSplitterChildrenParams>;
  onChange: TOnChange;
  // selection: ISelectionState;
  // value: string;
}

export function TipTapNoteSplitterExtension({ note, children }: INoteSplitterExtensionProps) {
  const modalsContext = useContext(ModalsContext);
  const { addNewNote } = useContext(EditabilityContext);

  const handleShowSplitModal = useCallback(
    (splitFunc: (text: string) => void) => {
      const Modal = showSplitModal(splitFunc);

      modalsContext.setModal({
        closeAfterAction: true,
        customView: Modal,
      });
    },
    [modalsContext],
  );

  const splitNoteIntoTwoParts = useCallback(
    (customName: string, newText: string, placeBefore: boolean) => {
      if (!note) {
        return;
      }

      addNewNote({
        parentId: note.parentId,
        name: customName,
        initialText: newText,
        placeConfig: {
          cursorId: note.id,
          placeBefore,
        },
      });
    },
    [note, addNewNote],
  );

  const handleSplitNoteAbove = useCallback(
    (newText: string, callbackFn: () => void) => {
      handleShowSplitModal((name) => {
        splitNoteIntoTwoParts(name, newText, true);
        callbackFn();
      });
    },
    [handleShowSplitModal, splitNoteIntoTwoParts],
  );

  const handleSplitNoteBelow = useCallback(
    (newText: string, callbackFn: () => void) => {
      handleShowSplitModal((name) => {
        splitNoteIntoTwoParts(name, newText, false);
        callbackFn();
      });
    },
    [handleShowSplitModal, splitNoteIntoTwoParts],
  );

  const params: INoteSplitterChildrenParams = useMemo(
    () => ({
      onSplitNoteAbove: handleSplitNoteAbove,
      onSplitNoteBelow: handleSplitNoteBelow,
    }),
    [handleSplitNoteAbove, handleSplitNoteBelow],
  );

  return children(params);
}
